import GetIcon from "@/components/font-awesome-icon";
import UIPopper from "@/components/popover";
import TruncateTooltip from "@/components/truncate-with-tooltip";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Event, customEventStyling } from "./event";
import { formatTime } from "@/services/generic.methods";
import { useCallback, useEffect, useMemo, useState } from "react";

const ShowMorePopper = ({
  title = "",
  events = [],
  dayDate,
  onLoadDayEvents,
  contextMenuOptions = null,
  popOverComp,
  onHover,
  onSelectEvent,
  clickNodeSX = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [loadedEvents, setLoadedEvents] = useState([]);
  const dayKey = useMemo(() => {
    if (events?.[0]?.calendarDayKey) return events[0].calendarDayKey;
    const sourceDate = dayDate || events?.[0]?.start;
    return sourceDate ? formatTime(sourceDate, "YYYY-MM-DD") : "";
  }, [dayDate, events]);

  const selectedDate = useMemo(() => {
    const sourceDate = dayDate || events?.[0]?.start;
    return sourceDate ? formatTime(sourceDate, "ddd MMMM DD") : "No Date";
  }, [dayDate, events]);

  const displayedEvents = loadedEvents.length ? loadedEvents : events;

  const loadDayEvents = useCallback(async () => {
    if (!onLoadDayEvents) return;
    const sourceDate = dayDate || events?.[0]?.start;
    if (!sourceDate) return;
    try {
      setLoading(true);
      const response = await onLoadDayEvents({ date: sourceDate, events });
      setLoadedEvents(response || []);
    } finally {
      setLoading(false);
    }
  }, [dayDate, events, onLoadDayEvents]);

  useEffect(() => {
    setLoadedEvents([]);
  }, [dayKey]);

  return (
    <UIPopper
      paperStyles={{ p: 1 }}
      anchorOrigin={{ vertical: "center", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      paperClasses=""
      clickNode={
        <Box
          color="custom.secondary_button_text"
          onClick={() => {
            if (!loadedEvents.length && onLoadDayEvents) loadDayEvents();
          }}
          sx={{ mt: 0.3, px: 2, py: 0.3, borderRadius: "5px", ...clickNodeSX }}
          bgcolor={"custom.container"}
        >
          {title}
        </Box>
      }
    >
      {({ setAnchorEl }) => {
        return (
          <Box sx={{ py: 1.5, pl: 1.5, pr: 0.5 }}>
            <Box
              className="d-flex justify-content-space-between"
              sx={{ pb: 1, pr: 0.5 }}
            >
              <Typography sx={{ fontWeight: 600 }} color={"custom.sub_heading"}>
                <TruncateTooltip text={selectedDate} limit={35} />
              </Typography>
              <GetIcon
                iconStyles={{ fontSize: "1rem" }}
                icon={faTimes}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setAnchorEl(null);
                }}
              />
            </Box>
            <Box
              className="custom-scroll"
              sx={{
                height: "fit-content",
                overflowY: "auto",
                width: "200px",
                maxHeight: "200px",
              }}
            >
              {loading ? (
                <Box sx={{ py: 2 }} className="d-flex justify-content-center">
                  <CircularProgress size={20} />
                </Box>
              ) : (
                displayedEvents.map((event) => (
                  <Box
                    className="cursor-pointer"
                    key={event.id}
                    sx={{ ...customEventStyling(event).style, mb: 0.5, px: 0.5 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (onSelectEvent) onSelectEvent(event, "event-clicked");
                    }}
                  >
                    <Event
                      event={event}
                      contextMenuOptions={contextMenuOptions}
                      popOverComp={popOverComp}
                      onHover={onHover}
                    />
                  </Box>
                ))
              )}
            </Box>
          </Box>
        );
      }}
    </UIPopper>
  );
};

export default ShowMorePopper;
